<template>
  <el-dialog
    :title="dialogTitle"
    custom-class="inbound-defect-action-dialog"
    :visible.sync="dialogVisible"
    :width="showRefundPanel ? '980px' : '960px'"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
  >
    <div v-if="showRefundPanel" class="dialog-section refund-section">
      <div class="section-title">退款列表</div>
      <el-table
        :data="refundList"
        border
        stripe
        size="mini"
        max-height="260"
        show-summary
        :summary-method="getRefundSummaries"
        v-loading="loading"
        element-loading-text="加载中..."
        class="defect-action-table refund-action-table"
      >
        <el-table-column type="index" label="序号" width="48" align="center"></el-table-column>
        <el-table-column prop="productName" label="产品名称" min-width="140" show-overflow-tooltip></el-table-column>
        <el-table-column prop="unitPrice" label="单价" width="90" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.unitPrice) }}</template>
        </el-table-column>
        <el-table-column prop="refundQuantity" label="退款数量" width="90" align="right"></el-table-column>
        <el-table-column prop="expectedRefundAmount" label="应退款金额" width="110" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.expectedRefundAmount) }}</template>
        </el-table-column>
        <el-table-column prop="actualRefundAmount" label="实退金额" width="130" align="right">
          <template slot-scope="scope">
            <el-input
              v-model="scope.row.actualRefundAmount"
              size="mini"
              type="number"
              class="actual-refund-input"
              @change="onActualRefundChange(scope.row)"
            ></el-input>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div class="dialog-section">
      <div class="section-title">{{ skuSectionTitle }}</div>
      <el-table
        :data="skuList"
        border
        stripe
        size="mini"
        max-height="280"
        show-summary
        :summary-method="getSkuSummaries"
        v-loading="loading"
        element-loading-text="加载中..."
        class="defect-action-table"
      >
        <el-table-column type="index" label="序号" width="48" align="center"></el-table-column>
        <el-table-column prop="sku" label="SKU" min-width="130" show-overflow-tooltip></el-table-column>
        <el-table-column prop="productName" label="产品名称" min-width="100" show-overflow-tooltip></el-table-column>
        <el-table-column prop="purchaseColorName" label="颜色" width="72" align="center" show-overflow-tooltip></el-table-column>
        <el-table-column prop="model" label="型号" width="72" align="center" show-overflow-tooltip></el-table-column>
        <el-table-column prop="purchaseQuantity" label="采购数量" width="80" align="right"></el-table-column>
        <el-table-column prop="actionQty" :label="actionQtyLabel" width="110" align="right"></el-table-column>
        <el-table-column
          v-if="showQualityFail"
          prop="qualityFail"
          label="质检不合格"
          width="96"
          align="right"
        ></el-table-column>
        <el-table-column
          v-if="showOutOfStock"
          prop="outOfStock"
          label="缺货数量"
          width="88"
          align="right"
        ></el-table-column>
        <el-table-column
          v-if="showOverStock"
          prop="overStock"
          label="多到数量"
          width="88"
          align="right"
        ></el-table-column>
      </el-table>
    </div>

    <div v-if="showReturnPanel" class="dialog-section return-section">
      <div class="section-title">退货明细（SKU）</div>
      <el-table
        :data="returnSkuList"
        border
        stripe
        size="mini"
        max-height="280"
        show-summary
        :summary-method="getReturnSkuSummaries"
        v-loading="loading"
        element-loading-text="加载中..."
        class="defect-action-table"
      >
        <el-table-column type="index" label="序号" width="48" align="center"></el-table-column>
        <el-table-column prop="sku" label="SKU" min-width="130" show-overflow-tooltip></el-table-column>
        <el-table-column prop="productName" label="产品名称" min-width="100" show-overflow-tooltip></el-table-column>
        <el-table-column prop="purchaseColorName" label="颜色" width="72" align="center" show-overflow-tooltip></el-table-column>
        <el-table-column prop="model" label="型号" width="72" align="center" show-overflow-tooltip></el-table-column>
        <el-table-column prop="purchaseQuantity" label="采购数量" width="80" align="right"></el-table-column>
        <el-table-column prop="returnQuantity" label="退货数量" width="110" align="right"></el-table-column>
        <el-table-column prop="qualityFail" label="质检不合格" width="96" align="right"></el-table-column>
        <el-table-column prop="overStock" label="多到数量" width="88" align="right"></el-table-column>
      </el-table>
      <div v-if="!loading && !returnSkuList.length" class="empty-return-tip">无可退货明细（需质检不合格或未处理多到）</div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" size="small" :loading="submitting" @click="submit">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { requestModules } from "@r";
import { QUALITY_DISPOSITION, RETURN_STATUS } from "./purchaseOrderStatus.js";

/** 发起退货：未处理多到 + 已选退货退款且待补退货的质检 */
const START_RETURN_RULE = {
  filter(row) {
    const returnCode = Number(row.returnStatus);
    if (returnCode === RETURN_STATUS.PENDING || returnCode === RETURN_STATUS.DONE) return false;
    const over =
      (Number(row.overStock) || 0) > 0 && !row.overStockConfirmed && !row.overStockReturned;
    const disposition = Number(row.qualityDisposition);
    const qualityPending =
      (Number(row.qualityFail) || 0) > 0 &&
      disposition === QUALITY_DISPOSITION.RETURN_AND_REFUND &&
      (returnCode === RETURN_STATUS.NONE || row.returnStatus === "" || row.returnStatus == null);
    return over || qualityPending;
  },
  calcQty(row) {
    const returnCode = Number(row.returnStatus);
    const disposition = Number(row.qualityDisposition);
    const quality =
      (Number(row.qualityFail) || 0) > 0 &&
      disposition === QUALITY_DISPOSITION.RETURN_AND_REFUND &&
      (returnCode === RETURN_STATUS.NONE || row.returnStatus === "" || row.returnStatus == null)
        ? Number(row.qualityFail) || 0
        : 0;
    const over =
      (Number(row.overStock) || 0) > 0 && !row.overStockConfirmed && !row.overStockReturned
        ? Number(row.overStock) || 0
        : 0;
    return quality + over;
  },
};

/** 退货退款中的退货明细：质检不合格 + 多到 */
const RETURN_REFUND_RETURN_RULE = {
  filter(row) {
    return (
      (Number(row.qualityFail) || 0) > 0 ||
      ((Number(row.overStock) || 0) > 0 && !row.overStockConfirmed && !row.overStockReturned)
    );
  },
  calcQty(row) {
    const over =
      (Number(row.overStock) || 0) > 0 && !row.overStockConfirmed && !row.overStockReturned
        ? Number(row.overStock) || 0
        : 0;
    return (Number(row.qualityFail) || 0) + over;
  },
};

/** 带退款操作：质检不合格与缺货均参与退款明细/数量 */
const REFUND_QTY_RULE = {
  filter(row) {
    return (Number(row.qualityFail) || 0) > 0 || (Number(row.outOfStock) || 0) > 0;
  },
  calcQty(row) {
    return (Number(row.qualityFail) || 0) + (Number(row.outOfStock) || 0);
  },
};

const ACTION_CONFIG = {
  startReturnRefund: {
    title: "发起退货退款",
    qtyLabel: "退货退款数量",
    showQualityFail: true,
    showOutOfStock: true,
    showRefundPanel: true,
    showReturnPanel: true,
    filter: REFUND_QTY_RULE.filter,
    calcQty: REFUND_QTY_RULE.calcQty,
  },
  skipReturnAndRefund: {
    title: "无需退货退款",
    qtyLabel: "无需退货退款数量",
    showQualityFail: true,
    showOutOfStock: true,
    filter(row) {
      return (Number(row.qualityFail) || 0) > 0;
    },
    calcQty(row) {
      return Number(row.qualityFail) || 0;
    },
  },
  skipReturnNeedRefund: {
    title: "无需退货仅退款",
    qtyLabel: "退款数量",
    showQualityFail: true,
    showOutOfStock: true,
    showRefundPanel: true,
    filter: REFUND_QTY_RULE.filter,
    calcQty: REFUND_QTY_RULE.calcQty,
  },
  startRefund: {
    title: "发起退款",
    qtyLabel: "退款数量",
    showQualityFail: true,
    showOutOfStock: true,
    showRefundPanel: true,
    filter: REFUND_QTY_RULE.filter,
    calcQty: REFUND_QTY_RULE.calcQty,
  },
  startReturn: {
    title: "发起退货",
    qtyLabel: "退货数量",
    showQualityFail: true,
    showOutOfStock: false,
    showOverStock: true,
    filter: START_RETURN_RULE.filter,
    calcQty: START_RETURN_RULE.calcQty,
  },
  confirmInbound: {
    title: "确认入库",
    qtyLabel: "确认入库数量",
    showQualityFail: false,
    showOutOfStock: false,
    showOverStock: true,
    filter(row) {
      const returnStatus = Number(row.returnStatus);
      if (returnStatus === 2 || returnStatus === 3) return false; // PENDING / DONE
      return (Number(row.overStock) || 0) > 0 && !row.overStockConfirmed && !row.overStockReturned;
    },
    calcQty(row) {
      return Number(row.overStock) || 0;
    },
  },
};

export default {
  name: "InboundDefectActionDialog",
  data() {
    return {
      dialogVisible: false,
      loading: false,
      submitting: false,
      actionKey: "",
      skuList: [],
      refundList: [],
      returnSkuList: [],
    };
  },
  computed: {
    config() {
      return ACTION_CONFIG[this.actionKey] || null;
    },
    dialogTitle() {
      return (this.config && this.config.title) || "";
    },
    actionQtyLabel() {
      return (this.config && this.config.qtyLabel) || "数量";
    },
    showQualityFail() {
      return !!(this.config && this.config.showQualityFail);
    },
    showOutOfStock() {
      return !!(this.config && this.config.showOutOfStock);
    },
    showOverStock() {
      return !!(this.config && this.config.showOverStock);
    },
    showRefundPanel() {
      return !!(this.config && this.config.showRefundPanel);
    },
    showReturnPanel() {
      return !!(this.config && this.config.showReturnPanel);
    },
    skuSectionTitle() {
      return this.showRefundPanel ? "退款明细（SKU）" : "明细";
    },
  },
  methods: {
    open(actionKey, selectedList) {
      if (!ACTION_CONFIG[actionKey]) {
        this.$message.warning("未知操作");
        return;
      }
      this.actionKey = actionKey;
      this.dialogVisible = true;
      this.skuList = [];
      this.refundList = [];
      this.returnSkuList = [];
      this.loadSkus(selectedList || []);
    },
    loadSkus(selectedList) {
      this.loading = true;
      requestModules
        .fetchPurchaseOrderSelectedSkus({
          selectedList: JSON.stringify(selectedList),
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "获取采购明细失败");
            this.dialogVisible = false;
            return;
          }
          const cfg = this.config;
          const allRows = res.ldata || [];
          this.skuList = allRows
            .filter(row => cfg.filter(row))
            .map(row => ({
              ...row,
              actionQty: cfg.calcQty(row),
            }));
          if (!this.skuList.length) {
            this.$message.warning(`没有符合「${cfg.title}」条件的采购记录`);
            this.dialogVisible = false;
            return;
          }
          if (this.showRefundPanel) {
            // 统一用材料维 productRefundLines（与采购单 productLines 同口径）；失败时回退 SKU 汇总
            const productRefundLines = (res.mdata && res.mdata.productRefundLines) || [];
            this.refundList = productRefundLines.length
              ? productRefundLines.map(row => ({
                  ...row,
                  unitPrice: parseFloat(row.unitPrice) || 0,
                  refundQuantity: Number(row.refundQuantity) || 0,
                  expectedRefundAmount: parseFloat(row.expectedRefundAmount) || 0,
                  actualRefundAmount:
                    row.actualRefundAmount !== undefined && row.actualRefundAmount !== null && row.actualRefundAmount !== ""
                      ? parseFloat(row.actualRefundAmount) || 0
                      : parseFloat(row.expectedRefundAmount) || 0,
                }))
              : this.buildRefundList(this.skuList);
          }
          if (this.showReturnPanel) {
            // 退货退款：质检 + 多到；从全量明细筛，避免被退款筛选条件截断
            this.returnSkuList = this.buildReturnSkuList(allRows);
          }
        })
        .catch(() => {
          this.$message.error("获取采购明细失败");
          this.dialogVisible = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildRefundList(skuList) {
      const map = new Map();
      (skuList || []).forEach(row => {
        const productName = row.productName || "未知产品";
        const key = `${productName}||${row.productType != null ? row.productType : ""}`;
        if (!map.has(key)) {
          map.set(key, {
            productName,
            productType: row.productType,
            unitPrice: parseFloat(row.unitPrice) || 0,
            refundQuantity: 0,
          });
        }
        const item = map.get(key);
        item.refundQuantity += Number(row.actionQty) || 0;
        if (!(Number(item.unitPrice) > 0) && Number(row.unitPrice) > 0) {
          item.unitPrice = parseFloat(row.unitPrice) || 0;
        }
      });
      return Array.from(map.values()).map(row => {
        const unitPrice = parseFloat(row.unitPrice) || 0;
        const refundQuantity = Number(row.refundQuantity) || 0;
        const expectedRefundAmount = parseFloat((unitPrice * refundQuantity).toFixed(2));
        return {
          ...row,
          unitPrice,
          refundQuantity,
          expectedRefundAmount,
          actualRefundAmount: expectedRefundAmount,
        };
      });
    },
    buildReturnSkuList(allRows) {
      return (allRows || [])
        .filter(row => RETURN_REFUND_RETURN_RULE.filter(row))
        .map(row => ({
          ...row,
          returnQuantity: RETURN_REFUND_RETURN_RULE.calcQty(row),
        }))
        .filter(row => (Number(row.returnQuantity) || 0) > 0);
    },
    formatMoney(value) {
      if (value === "" || value === null || value === undefined) {
        return "0.00";
      }
      return parseFloat(value).toFixed(2);
    },
    onActualRefundChange(row) {
      if (!row) return;
      const amount = parseFloat(row.actualRefundAmount);
      row.actualRefundAmount = Number.isNaN(amount) ? 0 : parseFloat(amount.toFixed(2));
    },
    getSkuSummaries({ columns, data }) {
      const sumProps = ["purchaseQuantity", "actionQty", "qualityFail", "outOfStock", "overStock"];
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "合计";
          return;
        }
        if (sumProps.includes(column.property)) {
          sums[index] = (data || []).reduce((total, row) => total + (Number(row[column.property]) || 0), 0);
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    getReturnSkuSummaries({ columns, data }) {
      const sumProps = ["purchaseQuantity", "returnQuantity", "qualityFail", "overStock"];
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "合计";
          return;
        }
        if (sumProps.includes(column.property)) {
          sums[index] = (data || []).reduce((total, row) => total + (Number(row[column.property]) || 0), 0);
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    getRefundSummaries({ columns, data }) {
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "合计";
          return;
        }
        if (column.property === "refundQuantity") {
          sums[index] = (data || []).reduce((total, row) => total + (Number(row.refundQuantity) || 0), 0);
          return;
        }
        if (["expectedRefundAmount", "actualRefundAmount"].includes(column.property)) {
          const total = (data || []).reduce((sum, row) => sum + (parseFloat(row[column.property]) || 0), 0);
          sums[index] = this.formatMoney(total);
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    submit() {
      if (!this.skuList.length) {
        this.$message.warning("没有可提交的采购记录");
        return;
      }
      if (this.showRefundPanel) {
        const invalid = (this.refundList || []).find(row => {
          const amount = parseFloat(row.actualRefundAmount);
          return Number.isNaN(amount) || amount < 0;
        });
        if (invalid) {
          this.$message.warning("实退金额不能为负数");
          return;
        }
      }
      this.submitting = true;
      const payload = {
        action: this.actionKey,
        selectedList: JSON.stringify(this.skuList),
      };
      if (this.showRefundPanel) {
        payload.refundList = JSON.stringify(this.refundList);
      }
      requestModules
        .transitionPurchaseOrderStatus(payload)
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "操作失败");
            return;
          }
          const mdata = res.mdata || {};
          const hasRefund = (mdata.refundGroupIds || []).length > 0;
          const hasReturn = (mdata.returnGroupIds || []).length > 0;
          if (this.actionKey === "startReturnRefund") {
            if (hasRefund && hasReturn) {
              this.$message.success("已发起退货退款，请到退款管理与退货管理分别确认");
            } else if (hasRefund) {
              this.$message.success("已发起退货退款，请到退款管理确认退款信息");
            } else {
              this.$message.success(res.message || "操作成功");
            }
          } else if (this.actionKey === "skipReturnNeedRefund" && hasRefund) {
            this.$message.success(res.message || "已标记无需退货仅退款，请到退款管理确认完成后结清");
          } else if (this.actionKey === "startRefund" && hasRefund) {
            this.$message.success(res.message || "已发起退款，请到退款管理确认退款完成后结清");
          } else if (this.actionKey === "startReturn" && hasReturn) {
            this.$message.success(res.message || "已发起退货，请到退货管理确认退货完成后结清");
          } else if (
            this.actionKey === "confirmInbound" ||
            this.actionKey === "skipReturnAndRefund"
          ) {
            this.$message.success(res.message || "操作成功");
          } else {
            this.$message.success(res.message || "操作成功");
          }
          this.dialogVisible = false;
          this.$emit("success");
        })
        .catch(() => {
          this.$message.error("操作失败");
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    handleClose() {
      this.skuList = [];
      this.refundList = [];
      this.returnSkuList = [];
      this.actionKey = "";
    },
  },
};
</script>

<style lang="scss">
.inbound-defect-action-dialog {
  .el-dialog__body {
    overflow-x: hidden;
    padding: 12px 16px;
  }

  .dialog-section {
    & + .dialog-section {
      margin-top: 16px;
    }
  }

  .section-title {
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #303133;
  }

  .empty-return-tip {
    margin-top: 8px;
    font-size: 12px;
    color: #909399;
  }

  .defect-action-table {
    width: 100%;

    .el-table__body-wrapper,
    .el-table__header-wrapper,
    .el-table__footer-wrapper {
      overflow-x: hidden !important;
    }
  }

  .actual-refund-input {
    width: 110px;

    .el-input__inner {
      text-align: right;
      padding-right: 8px;
    }
  }
}
</style>
